import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useApolloClient } from "@apollo/react-hooks";
import { saveAs } from "file-saver";

import { AuthContext } from "../../context/AuthProvider";
import { EditorContext } from "../context/EditorContext/EditorProvider";
import {
  SET_WAIT_MESSAGE,
  SetWaitMessageAction
} from "../actions/actionTypes";
import { StemFidelities, StemFidelity } from "../store";
import { isDownloadLimitError } from "../../utils/downloadGate";
import { promptSignupGate } from "../../components/SignupGatePrompt";
import { INTENTS } from "../../utils/postLoginRedirect";
import { CREATE_DOWNLOAD_MUTATION_WITHOUT_TEAM } from "../../components/TrackCheckoutDetail/queries";
import { NewTrackSubscribeContext } from "../../context/NewTrackSubscribeContext";

/**
 * Returns a `downloadMix` function that renders the current editor mix at
 * the appropriate fidelity, records the download (gated for anonymous users),
 * and either saves the file or prompts the signup gate. Used by both the
 * editor header download icon and the main-menu Download entry so the logic
 * stays in one place.
 */
export function useDownloadMix() {
  const dispatch = useDispatch();
  const user = useContext(AuthContext);
  const editor = useContext(EditorContext);
  const history = useHistory();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const newTrackGate = useContext(NewTrackSubscribeContext);

  return () => {
    // Anonymous/new-track gate: if the subscribe gate intercepts this track,
    // it handles the prompt and we skip the mixdown entirely.
    if (newTrackGate.requestDownload(editor.initialTrack)) return;

    let stemFidelity: StemFidelity = StemFidelities.SD;
    const hasPaidAccess = user.hasPaidTrackAccess(editor.initialTrack);
    const userLoggedIn = user.isUserPrivate();
    const isUserAdmin = user.isUserAdmin();
    const isContentReviewer = user.isUserContentReviewer();
    // Use HD stems if the user has paid access, is an admin, or is a content
    // reviewer. We don't need lossless stems here — that's reserved for
    // rendering variations.
    if (userLoggedIn && (hasPaidAccess || isUserAdmin || isContentReviewer)) {
      stemFidelity = StemFidelities.HD;
    }

    // after signup, reopen the editor on this track (its mix restores from cache)
    const reopenEditorIntent = editor.initialTrack?.id
      ? { type: INTENTS.EDITOR, trackId: editor.initialTrack.id }
      : undefined;

    editor.requestMixdown(
      stemFidelity,
      (blob, filename) => {
        // Record the download; if the anonymous gate rejects, prompt signup
        // instead of saving the mixdown.
        apolloClient
          .mutate({
            mutation: CREATE_DOWNLOAD_MUTATION_WITHOUT_TEAM,
            variables: {
              user: user?.user?.id ?? null,
              track: editor.initialTrack?.id,
              details: filename || "editor mixdown"
            }
          })
          .then(result => {
            if (isDownloadLimitError(result)) {
              editor.close();
              promptSignupGate(history, location, reopenEditorIntent);
            } else {
              saveAs(blob, filename);
            }
          })
          .catch(err => {
            if (isDownloadLimitError(err)) {
              editor.close();
              promptSignupGate(history, location, reopenEditorIntent);
            } else {
              // Non-gate failure: still let the user have the file.
              saveAs(blob, filename);
            }
          });
        // Hide loading message
        dispatch<SetWaitMessageAction>({
          type: SET_WAIT_MESSAGE,
          id: "PERFORMING_MIXDOWN",
          role: "processing",
          message: null
        });
      },
      () => {
        dispatch<SetWaitMessageAction>({
          type: SET_WAIT_MESSAGE,
          id: "PERFORMING_MIXDOWN",
          role: "processing",
          message: "Rendering Custom Mix..."
        });
      }
    );
  };
}
